import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { 
  ImageCheckPlayerResult, 
  ImageCheckOperationSummary,
  ImageCheckOperationState 
} from '../../models/image-check.models';
import { LoggingService } from '../shared/logging/logging.service';
import { ImageCheckStorageService } from './image-check-storage.service';
import { ConfigService } from '../config/config.service';

/**
 * Service for managing image check operations
 * Handles HTTP requests, batching, pause/resume, and state management
 */
@Injectable({
  providedIn: 'root'
})
export class ImageCheckService {
  private readonly DEFAULT_TIMEOUT_MS = 10000; // 10 seconds
  private readonly DEFAULT_BATCH_SIZE = 5;
  private readonly BATCH_DELAY_MS = 100;

  // Operation state
  private operationState = signal<ImageCheckOperationState>('idle');
  readonly operationState$ = this.operationState.asReadonly();

  // Player results
  private playerResults = signal<ImageCheckPlayerResult[]>([]);
  readonly playerResults$ = this.playerResults.asReadonly();

  // Configuration
  private endpointUrl = signal<string>('');
  readonly endpointUrl$ = this.endpointUrl.asReadonly();

  // Cancellation & control
  private isCancelled = false;
  private isPaused = false;
  private pauseResolve: (() => void) | null = null;

  constructor(
    private http: HttpClient,
    private loggingService: LoggingService,
    private storageService: ImageCheckStorageService,
    private configService: ConfigService
  ) {
    this.loadEndpointSettings();
  }

  /**
   * Load endpoint settings from storage or use default from config
   */
  loadEndpointSettings(): void {
    const stored = this.storageService.getStoredEndpoint();
    if (stored) {
      this.endpointUrl.set(stored);
      this.loggingService.info('ImageCheck', 'Loaded endpoint from localStorage', { endpoint: stored });
    } else {
      const defaultEndpoint = this.configService.config?.imageCheckEndpoint || 'https://images.lpga.org/players';
      this.endpointUrl.set(defaultEndpoint);
      this.loggingService.info('ImageCheck', 'Using default endpoint from config', { endpoint: defaultEndpoint });
    }
  }

  /**
   * Set the endpoint URL and persist to storage
   * @param url The endpoint URL
   */
  setEndpointUrl(url: string): void {
    this.endpointUrl.set(url);
    this.storageService.setStoredEndpoint(url);
    this.loggingService.info('ImageCheck', 'Endpoint URL updated', { endpoint: url });
  }

  /**
   * Get the current endpoint URL
   * @returns The current endpoint URL
   */
  getEndpointUrl(): string {
    return this.endpointUrl();
  }

  /**
   * Validate the endpoint by checking if NoID.jpg exists
   * @param endpointUrl The endpoint URL to validate
   * @returns Promise that resolves with validation result (true if valid, false otherwise)
   */
  async validateEndpoint(endpointUrl: string): Promise<{ isValid: boolean; error?: string }> {
    const testUrl = `${endpointUrl}/NoID.jpg`;
    this.loggingService.info('ImageCheck', 'Validating endpoint with NoID.jpg', { testUrl });

    try {
      const imageExists = await this.checkImageViaImgElement(testUrl, this.DEFAULT_TIMEOUT_MS);
      
      if (imageExists) {
        this.loggingService.info('ImageCheck', 'Endpoint validation successful', { endpoint: endpointUrl });
        return { isValid: true };
      } else {
        this.loggingService.warn('ImageCheck', 'Endpoint validation failed - NoID.jpg not found', { endpoint: endpointUrl });
        return { isValid: false, error: 'NoID.jpg not found at endpoint' };
      }
    } catch (error) {
      const errorMessage = (error as any).message === 'Timeout' 
        ? 'Endpoint validation timeout' 
        : 'Endpoint validation error';
      this.loggingService.error('ImageCheck', errorMessage, { endpoint: endpointUrl, error });
      return { isValid: false, error: errorMessage };
    }
  }

  /**
   * Check images for all players
   * @param players Array of players to check (must have id, firstName, lastName)
   * @param endpointUrl Base endpoint URL
   * @param onProgress Optional callback for progress updates
   * @returns Summary of the operation
   */
  async checkImages(
    players: Array<{ id: string; firstName: string; lastName: string }>,
    endpointUrl: string,
    onProgress?: (result: ImageCheckPlayerResult) => void
  ): Promise<ImageCheckOperationSummary> {
    const startTime = Date.now();
    this.operationState.set('running');
    this.isCancelled = false;
    this.isPaused = false;

    this.loggingService.info('ImageCheck', 'Starting image check', {
      playerCount: players.length,
      endpoint: endpointUrl
    });

    // Initialize results
    const results: ImageCheckPlayerResult[] = players.map(p => ({
      playerId: p.id,
      firstName: p.firstName,
      lastName: p.lastName,
      status: 'pending',
      attempts: 0
    }));
    this.playerResults.set(results);

    try {
      // Process in batches
      for (let i = 0; i < results.length; i += this.DEFAULT_BATCH_SIZE) {
        if (this.isCancelled) {
          this.loggingService.info('ImageCheck', 'Operation cancelled by user');
          break;
        }

        // Handle pause
        while (this.isPaused && !this.isCancelled) {
          await new Promise<void>(resolve => {
            this.pauseResolve = resolve;
          });
        }

        if (this.isCancelled) break;

        const batch = results.slice(i, i + this.DEFAULT_BATCH_SIZE);
        const batchPromises = batch.map(result => 
          this.checkImageForPlayer(result, endpointUrl, this.DEFAULT_TIMEOUT_MS)
            .then(updated => {
              // Update the result in the array
              const index = results.findIndex(r => r.playerId === updated.playerId);
              if (index !== -1) {
                results[index] = updated;
                this.playerResults.set([...results]);
                if (onProgress) {
                  onProgress(updated);
                }
              }
              return updated;
            })
        );

        await Promise.all(batchPromises);

        // Delay between batches (except for last batch)
        if (i + this.DEFAULT_BATCH_SIZE < results.length && !this.isCancelled) {
          await this.delay(this.BATCH_DELAY_MS);
        }
      }

      const durationMs = Date.now() - startTime;
      const successCount = results.filter(r => r.status === 'success').length;
      const failedCount = results.filter(r => r.status === 'failed').length;

      this.operationState.set('completed');

      const summary: ImageCheckOperationSummary = {
        totalPlayers: players.length,
        successCount,
        failedCount,
        durationMs,
        completedAt: new Date(),
        endpoint: endpointUrl
      };

      this.loggingService.info('ImageCheck', 'Operation completed', summary);

      return summary;
    } catch (error) {
      this.loggingService.error('ImageCheck', 'Operation failed', error);
      this.operationState.set('idle');
      throw error;
    }
  }

  /**
   * Check image for a single player using Image element loading
   * This approach bypasses CORS restrictions that would block HTTP requests to CDN endpoints
   * @param result The player result to update
   * @param endpoint Base endpoint URL
   * @param timeoutMs Timeout in milliseconds
   * @returns Updated player result
   */
  private async checkImageForPlayer(
    result: ImageCheckPlayerResult,
    endpoint: string,
    timeoutMs: number
  ): Promise<ImageCheckPlayerResult> {
    const updated = { ...result };
    updated.status = 'checking';
    updated.attempts = (updated.attempts || 0) + 1;

    // Build URL - pad LPGA ID to 6 characters with leading zeros and append .jpg
    const paddedId = result.playerId.padStart(6, '0');
    const url = `${endpoint}/${paddedId}.jpg`;
    
    this.loggingService.debug('ImageCheck', 'Checking image', { url, playerId: result.playerId, paddedId });

    try {
      const imageExists = await this.checkImageViaImgElement(url, timeoutMs);
      
      if (imageExists) {
        updated.status = 'success';
        updated.responseCode = 200;
        updated.checkedAt = new Date();
        this.loggingService.debug('ImageCheck', 'Image found', { playerId: result.playerId });
      } else {
        updated.status = 'failed';
        updated.responseCode = 404;
        updated.errorMessage = 'Image Not Found';
        updated.checkedAt = new Date();
        this.loggingService.debug('ImageCheck', 'Image not found', { playerId: result.playerId });
      }
    } catch (error) {
      updated.status = 'failed';
      updated.checkedAt = new Date();

      if ((error as any).message === 'Timeout') {
        updated.errorMessage = 'Timeout';
        this.loggingService.debug('ImageCheck', 'Request timeout', { playerId: result.playerId });
      } else {
        updated.errorMessage = 'Load Error';
        this.loggingService.debug('ImageCheck', 'Image load error', { 
          playerId: result.playerId,
          error 
        });
      }
    }

    return updated;
  }

  /**
   * Check if an image exists by loading it via an Image element
   * This bypasses CORS restrictions that would block HTTP/fetch requests to CDN endpoints like CloudFront
   * @param url The image URL to check
   * @param timeoutMs Timeout in milliseconds
   * @returns Promise that resolves to true if image loads successfully, false otherwise
   */
  private checkImageViaImgElement(url: string, timeoutMs: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      let timeoutId: ReturnType<typeof setTimeout>;

      const cleanup = () => {
        clearTimeout(timeoutId);
        img.onload = null;
        img.onerror = null;
      };

      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Timeout'));
      }, timeoutMs);

      img.onload = () => {
        cleanup();
        // Additional check: ensure image has valid dimensions (not a placeholder/error image)
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          resolve(true);
        } else {
          resolve(false);
        }
      };

      img.onerror = () => {
        cleanup();
        resolve(false);
      };

      // Start loading the image
      img.src = url;
    });
  }

  /**
   * Pause the current operation
   */
  pause(): void {
    if (this.operationState() === 'running') {
      this.isPaused = true;
      this.operationState.set('paused');
      this.loggingService.info('ImageCheck', 'Operation paused');
    }
  }

  /**
   * Resume a paused operation
   */
  resume(): void {
    if (this.operationState() === 'paused') {
      this.isPaused = false;
      this.operationState.set('running');
      if (this.pauseResolve) {
        this.pauseResolve();
        this.pauseResolve = null;
      }
      this.loggingService.info('ImageCheck', 'Operation resumed');
    }
  }

  /**
   * Stop the current operation
   */
  stop(): void {
    this.isCancelled = true;
    this.isPaused = false;
    this.operationState.set('idle');
    if (this.pauseResolve) {
      this.pauseResolve();
      this.pauseResolve = null;
    }
    this.loggingService.info('ImageCheck', 'Operation stopped');
  }

  /**
   * Reset all results and state
   */
  reset(): void {
    this.playerResults.set([]);
    this.operationState.set('idle');
    this.isCancelled = false;
    this.isPaused = false;
    this.pauseResolve = null;
  }

  /**
   * Format results as text table for clipboard export
   * @param results Player results to format
   * @param tournamentName Name of the tournament
   * @returns Formatted text table
   */
  formatClipboardExport(results: ImageCheckPlayerResult[], tournamentName: string): string {
    const header = `Image Check Results - ${tournamentName}\nChecked: ${new Date().toISOString()}\n\n`;
    const tableHeader = 'LPGA ID    First Name      Last Name            Status\n';
    const separator = '─'.repeat(70) + '\n';
    
    const rows = results.map(r => {
      const status = r.status === 'success' 
        ? `Success (${r.responseCode})`
        : r.status === 'failed'
        ? r.responseCode 
          ? `Failed (${r.responseCode})`
          : `Failed (${r.errorMessage})`
        : r.status;
      
      return `${r.playerId.padEnd(11)}${r.firstName.padEnd(16)}${r.lastName.padEnd(21)}${status}`;
    }).join('\n');

    const successCount = results.filter(r => r.status === 'success').length;
    const failedCount = results.filter(r => r.status === 'failed').length;
    const footer = `\n${separator}Total: ${results.length} players | Passed: ${successCount} | Failed: ${failedCount}`;

    return header + tableHeader + separator + rows + footer;
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

